import {Box, Divider} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {DomainListFragmentDoc, DomainListFragment} from "./queries.generated";
import {HasPermission, Link} from "../widgets";
import {GenericTable} from "@dvrt/graphql-widgets";
import {useConcertoGenericQuery} from "@/hooks";
import {faQuestionCircle, faMobile, faUserFriends, faStream} from "@fortawesome/free-solid-svg-icons";
import TableShortcutButton from "../TableShortcutButton";
import CreateButton from "@/components/domain/create/CreateButton";
import DeleteButton from "@/components/domain/delete/DeleteButton";
import DomainImport from "./import/DomainImport";
import {Grid} from "@mui/material";
import {FormattedMessage, defineMessage, defineMessages} from "react-intl";
import {DomainType} from "@/api";
import {commonTranslations, DOMAIN_TYPE_MESSAGES} from "@/utils/translations";

const useStyles = makeStyles()({
    body: {
        padding: 20,
    },
});

export default function DomainListPage() {
    const query = useConcertoGenericQuery<DomainListFragment>("domains", DomainListFragmentDoc, {
        filtering: {
            argumentName: "filter",
            placeHolder: commonTranslations.filterPlaceholders.nameOrDescription,
            idPrefix: "search-domains",
        },
        initialOrdering: ["name"],
    });
    const {classes} = useStyles();

    return (
        <div className={classes.body}>
            <GenericTable<DomainListFragment>
                query={query}
                labels={defineMessages({
                    name: {id: "domains.name", defaultMessage: "Name"},
                    description: {id: "domains.description", defaultMessage: "Description"},
                    domainType: {id: "domains.domainType", defaultMessage: "Type"},
                })}
                dataRenderers={{
                    name: (value, row) => {
                        return <Link to={`/domains/${row.id}`}>{value}</Link>;
                    },
                    domainType: (value: DomainType) => {
                        return <FormattedMessage {...DOMAIN_TYPE_MESSAGES[value]} />;
                    },
                }}
                extraColumns={[
                    {
                        path: "actions",
                        label: null,
                        renderer: (row) => {
                            return (
                                <Box sx={{display: "flex"}}>
                                    <TableShortcutButton
                                        tooltipTitle={defineMessage({
                                            id: "domain_shortcut.status",
                                            defaultMessage: "Status",
                                        })}
                                        shortcutPath={`/domains/${row.id}/status`}
                                        buttonIcon={faQuestionCircle}
                                    />
                                    <TableShortcutButton
                                        tooltipTitle={defineMessage({
                                            id: "domain_shortcut.extensions",
                                            defaultMessage: "Users",
                                        })}
                                        shortcutPath={`/domains/${row.id}/users`}
                                        buttonIcon={faMobile}
                                    />
                                    <TableShortcutButton
                                        tooltipTitle={defineMessage({
                                            id: "domain_shortcut.call-groups",
                                            defaultMessage: "Call groups",
                                        })}
                                        shortcutPath={`/domains/${row.id}/call-groups`}
                                        buttonIcon={faUserFriends}
                                    />
                                    <TableShortcutButton
                                        tooltipTitle={defineMessage({
                                            id: "domain_shortcut.call-queues",
                                            defaultMessage: "Call queues",
                                        })}
                                        shortcutPath={`/domains/${row.id}/call-queues`}
                                        buttonIcon={faStream}
                                    />
                                    <HasPermission name="delete_domain">
                                        <Divider orientation="vertical" flexItem style={{margin: "0 0.5rem"}} />
                                        <DeleteButton
                                            domainId={row.id}
                                            onSuccess={() => query.refetch()}
                                            name={row.name}
                                        />
                                    </HasPermission>
                                </Box>
                            );
                        },
                    },
                ]}
                createButton={
                    <HasPermission name="add_domain">
                        <Grid container spacing={2}>
                            <Grid>
                                <CreateButton
                                    successItemPath={"domains"}
                                    onSuccess={query.refetch}
                                    title={defineMessage({id: "newDomain", defaultMessage: "New domain"})}
                                />
                            </Grid>
                            <Grid>
                                <DomainImport refetch={query.refetch} />
                            </Grid>
                        </Grid>
                    </HasPermission>
                }
                orderableFields={["name", "description", "domainType"]}
            />
        </div>
    );
}
